import { useState, type FormEvent } from 'react';
import { createMatch, joinByCode, quickPlay } from '../../net/session';
import { useSessionStore } from '../../store/sessionStore';
import { useSettingsStore } from '../../store/settingsStore';
import { parseRoomCode } from './inviteCode';
import { OpenMatches } from './OpenMatches';
import type { HomeAction } from './PlayView';

interface Props {
  /** From an invite link: filled in, with Join as the one obvious action. */
  invite: string;
  pending: HomeAction | null;
  onAction: (action: HomeAction) => void;
}

/** Home → Join Lobby: a room code (or a pasted invite link) and the live open matches list. */
export function JoinView({ invite, pending, onAction }: Props) {
  const name = useSettingsStore((s) => s.name);
  const busy = useSessionStore((s) => s.status === 'connecting');
  const [code, setCode] = useState(invite);
  const invited = invite !== '';
  const label = (action: HomeAction, idle: string) =>
    busy && pending === action ? 'Connecting…' : idle;

  const join = (e: FormEvent) => {
    e.preventDefault();
    const room = parseRoomCode(code);
    if (!room) return;
    onAction('join');
    void joinByCode(room, name);
  };

  return (
    <>
      <form className={invited ? 'row code-row invite' : 'row code-row'} onSubmit={join}>
        <input
          value={code}
          placeholder="Room code or invite link"
          aria-label="Room code or invite link"
          autoFocus={!invited}
          onChange={(e) => setCode(e.target.value)}
        />
        <button
          className={invited ? 'primary' : undefined}
          disabled={busy || !code.trim()}
          aria-busy={busy && pending === 'join'}
          autoFocus={invited}
          type="submit"
        >
          {label('join', 'Join')}
        </button>
      </form>
      <OpenMatches
        disabled={busy}
        quickLabel={label('quick', 'Quick Match')}
        createLabel={label('create', 'Create Lobby')}
        onJoin={(roomId) => {
          onAction('browser');
          void joinByCode(roomId, name);
        }}
        onQuickMatch={() => {
          onAction('quick');
          void quickPlay(name);
        }}
        onCreate={() => {
          onAction('create');
          void createMatch(name, false);
        }}
      />
    </>
  );
}
